/**
 * Synced settings (`core.settings`, spec §8.1): Pomodoro, breaks, the assistant, onboarding. The levels (§9.24,
 * ADR 0019) and the comfort — theme, a11y (§9.19), the open-task count — belong to the person (ADR 0032):
 * `useLevels`, `useComfort`. `useSettings` lays the person's comfort over the workspace's settings.
 * Readers select from the doc; writers commit through `setSetting`.
 */
import { useMemo } from "react";
import { LoroMap, type LoroDoc } from "loro-crdt";
import { commitMessage } from "../core/actor";
import { C, type Level } from "../core/schema";
import { useDocSelector } from "../core/useDocSelector";
import type { DocHandle } from "../sync/docManager";
import { useComfort } from "./comfort";

export interface A11y {
  reducedMotion: boolean;
  muteBadges: boolean;
  plainColors: boolean;
  /** dotted lines around each dashboard widget, in the middle of the gaps */
  widgetLines: boolean;
}

export interface Settings {
  theme?: "system" | "light" | "dark";
  onboarded?: boolean;
  a11y: A11y;
  pomodoro: {
    workMin: number;
    shortMin: number;
    longMin: number;
    cyclesBeforeLong: number;
    /** pause the task timer during Pomodoro breaks, resume it when work starts again */
    autoPauseInBreaks?: boolean;
    /** pausing or resuming one of the two clocks does the same to the other */
    linkPauses?: boolean;
  };
  breaks: { everyMin: number; lengthMin: number };
  summary: { showOpenCount: boolean };
  assistant: { autoApply: boolean };
  idle: { promptAfterMin: number };
  /** drawing engine of every graph widget (ADR 0009) */
  graph: { renderer: "cytoscape" | "reactflow" };
}

const DEFAULTS: Settings = {
  theme: "system",
  onboarded: false,
  a11y: { reducedMotion: false, muteBadges: false, plainColors: false, widgetLines: false },
  pomodoro: { workMin: 25, shortMin: 5, longMin: 15, cyclesBeforeLong: 4 },
  breaks: { everyMin: 0, lengthMin: 5 },
  summary: { showOpenCount: false },
  assistant: { autoApply: false },
  idle: { promptAfterMin: 15 },
  graph: { renderer: "cytoscape" },
};

export function readSettings(doc: LoroDoc): Settings {
  const raw = doc.getMap(C.settings).toJSON() as Partial<Settings>;
  return {
    ...DEFAULTS,
    ...raw,
    a11y: { ...DEFAULTS.a11y, ...(raw.a11y ?? {}) },
    pomodoro: { ...DEFAULTS.pomodoro, ...(raw.pomodoro ?? {}) },
    graph: { ...DEFAULTS.graph, ...(raw.graph ?? {}) },
  };
}

export function useSettings(handle: DocHandle | null): Settings {
  const ws = useDocSelector(handle, (doc) => readSettings(doc)) ?? DEFAULTS;
  const c = useComfort();
  return useMemo(
    () => ({
      ...ws,
      theme: c.theme,
      a11y: {
        reducedMotion: c.reducedMotion,
        muteBadges: c.muteBadges,
        plainColors: c.plainColors,
        widgetLines: c.widgetLines,
      },
      summary: { showOpenCount: c.showOpenCount },
    }),
    [ws, c.theme, c.reducedMotion, c.muteBadges, c.plainColors, c.widgetLines, c.showOpenCount],
  );
}

/** `setSetting(doc, "a11y.reducedMotion", true)` — dotted paths into nested maps. */
export function setSetting(doc: LoroDoc, path: string, value: unknown): void {
  const parts = path.split(".");
  let m: LoroMap = doc.getMap(C.settings);
  for (const p of parts.slice(0, -1)) {
    const child = m.get(p);
    m =
      child && typeof child === "object" && "set" in child
        ? (child as LoroMap)
        : m.setContainer(p, new LoroMap());
  }
  m.set(parts[parts.length - 1], value as never);
  doc.commit({ message: commitMessage({ op: "setSetting", path }) });
}

export const LEVELS: Level[] = ["simple", "standard", "power"];

export function atLeast(level: Level, needed: Level): boolean {
  return LEVELS.indexOf(level) >= LEVELS.indexOf(needed);
}
